/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState, useEffect } from "react";
import { Box, FormControl, useTheme } from "@mui/material";
import { Control, Controller } from "react-hook-form";
import { MuiTelInput } from "mui-tel-input";

import ErrorMessage from "../ErrorMessage";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {formatPhoneNumberIntl} from "@/utils/formatPhoneNumberIntl.ts";

interface IProp {
  control: Control<any>;
  name: string;
  placeholder: string;
}

const PhoneInput: React.FC<IProp> = ({ control, name, placeholder }) => {
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement>(null);
  const telInputRef = useRef<HTMLInputElement>(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [inputWidth, setInputWidth] = useState<string>('86.6%');

  const updateInputWidth = () => {
    if (telInputRef.current) {
      const width = telInputRef.current.offsetWidth;
      setInputWidth(`${width}px`);
    }
  };

  useEffect(() => {
    updateInputWidth();
    
    const handleResize = () => {
      updateInputWidth();
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const { onChange, value, ...otherFields } = field;
        const formattedValues = formatPhoneNumberIntl(value);
        const defaultCountryCode = "+1";
        
        return (
          <FormControl
            component="label"
            error={!!fieldState.error}
            sx={{ position: "relative", width: "100%" }}
          >
            <input 
              type="text" 
              value={formattedValues || defaultCountryCode} 
              ref={inputRef}
              onChange={(e) => {
                const formattedValue = formatPhoneNumberIntl(e.target.value);
                onChange(formattedValue);
              }}
              onFocus={() => setInputFocused(true)} onBlur={() => setInputFocused(false)}
              style={{
                position: 'absolute',
                top: '2px',
                left: '62px',
                zIndex: 10,
                background: 'white',
                padding: '11px',
                fontSize: '1.125rem',
                fontWeight: 500,
                fontFamily: '"Mulish",sans-serif',
                minWidth: 0,
                width: `calc(${inputWidth} - 5%)`,
                outline: 'none',
                border: 'none',
                borderRadius: '0 8px 8px 0',
                WebkitAnimationName: 'mui-auto-fill-cancel',
                animationName: 'mui-auto-fill-cancel',
                WebkitAnimationDuration: '10ms',
                animationDuration: '10ms'
              }}
            />
            <MuiTelInput
              {...otherFields}
              variant="outlined"
              value={formattedValues || defaultCountryCode}
              onChange={(value) => {
                const formattedValue = formatPhoneNumberIntl(value);
                onChange(formattedValue);
              }}
              defaultCountry="US"
              sx={{
                "& .MuiTouchRipple-root": {
                  display: "none",
                },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: inputFocused ? theme.palette.primary.main : "#e5e5e5",
                    borderRadius: "8px",
                  },
                  "& input": {
                    borderLeft: "1px solid #e5e5e5",
                    color: 'transparent',
                  },
                  "& input::placeholder": {
                    color: theme.palette.grey[400],
                    opacity: 1,
                  },
                },

                "& .MuiPaper-root": {
                  maxHeight: "200px",
                  overflowY: "scroll",
                },

                "& .MuiInputBase-root": {
                  pl: 0,
                },
                "& .MuiIconButton-root": {
                  borderRadius: 0,
                  pr: 2.5,
                },
                "& .MuiIconButton-root:hover": {
                  borderRadius: 0,
                },

                "& .MuiTelInput-Flag": {
                  width: 24,
                  height: 24,
                  borderRadius: 100,
                  overflow: "hidden",
                },

                "& .MuiTelInput-Flag img": {
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                },
              }}
              placeholder={placeholder}
              inputProps={{
                style: {
                  padding: "12px",
                  fontSize: "1.125rem",
                  fontWeight: 500,
                  fontFamily: '"Mulish",sans-serif',
                },
                ref: telInputRef
              }}
            />
            <Box
              position="absolute"
              sx={{
                top: "50%",
                left: "32px",
                transform: "translateY(-50%)",
                zIndex: 1,
                pointerEvents: "none",
              }}
              width={24}
              height={24}
            >
              <KeyboardArrowDownIcon />
            </Box>
            {fieldState.error?.message && (
              <ErrorMessage message={fieldState.error.message} />
            )}
          </FormControl>
        );
      }}
    />
  );
};

export default PhoneInput;
